import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Loader2, FileText, User as UserIcon, MapPin, CreditCard, AlertTriangle, MessageSquare, ExternalLink, Shield, Banknote, Scale, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Admin Bookings tab with a click-to-open detail modal.
 *
 * The legacy inline table had no way to drill into a reservation. The modal
 * now shows every field admins need for support and dispute resolution:
 * reservation ID, guest, host, vehicle, dates, payment status + Stripe
 * payment intent, fee breakdown, host payout, RivoNes commission, booking
 * status, created timestamp. Admin actions are: cancel, mark dispute,
 * contact guest (opens internal inbox), contact host (opens internal inbox).
 */
export default function AdminBookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  // "Pending deposits" shortcut: when active, the table is replaced with
  // the dedicated overdue-deposit list (bookings whose 72h grace window
  // has elapsed, no open dispute). One-click release from each row.
  const [pendingDepositsMode, setPendingDepositsMode] = useState(false);
  const [pendingDepositsData, setPendingDepositsData] = useState(null); // {count, items}
  const [pendingCount, setPendingCount] = useState({ overdue: 0, upcoming: 0 });
  const [quickReleasingId, setQuickReleasingId] = useState(null);
  const [autoRunBusy, setAutoRunBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  // Deposit panel state — fetched lazily when the detail modal opens so we
  // always show fresh status (deposit_status, eligible_at, notes timeline).
  const [deposit, setDeposit] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositAction, setDepositAction] = useState(null); // 'release'|'partial'|'charge'|'dispute'|'note'
  const [depositActionBusy, setDepositActionBusy] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const [actionNote, setActionNote] = useState("");

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data?.items || data?.bookings || (Array.isArray(data) ? data : []));
    } catch (_e) {
      toast.error("No pudimos cargar las reservaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  // Pending-deposit count (light call) — drives the badge on the
  // "Depósitos por liberar" shortcut button. Refreshes after destructive
  // deposit actions so the badge stays accurate without a hard reload.
  const fetchPendingCount = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/deposits/pending/count");
      setPendingCount({ overdue: data.overdue || 0, upcoming: data.upcoming || 0 });
    } catch (_e) { /* silent */ }
  }, []);

  // Heavier fetch — full pending-deposit list with guest + vehicle joined
  // server-side so the table renders in one round-trip.
  const fetchPendingDeposits = useCallback(async () => {
    setPendingDepositsData(null);
    try {
      const { data } = await api.get("/admin/deposits/pending");
      setPendingDepositsData(data);
    } catch (_e) {
      toast.error("No pudimos cargar los depósitos pendientes");
      setPendingDepositsData({ count: 0, items: [] });
    }
  }, []);

  useEffect(() => { fetchBookings(); fetchPendingCount(); }, [fetchBookings, fetchPendingCount]);

  useEffect(() => {
    if (pendingDepositsMode) fetchPendingDeposits();
  }, [pendingDepositsMode, fetchPendingDeposits]);

  const quickRelease = async (bookingId) => {
    if (!window.confirm("¿Liberar el depósito completo al huésped?")) return;
    setQuickReleasingId(bookingId);
    try {
      await api.post(`/admin/bookings/${bookingId}/deposit/release`, {
        note: "Liberación rápida desde panel de depósitos pendientes",
      });
      toast.success("Depósito liberado");
      await Promise.all([fetchPendingDeposits(), fetchPendingCount(), fetchBookings()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos liberar el depósito");
    } finally {
      setQuickReleasingId(null);
    }
  };

  const runAutoReleaseSweep = async () => {
    setAutoRunBusy(true);
    try {
      const { data } = await api.post("/admin/deposits/auto-release/run");
      toast.success(
        `Sweep ejecutado: ${data.released} liberados, ${data.errors} con errores, ${data.candidates} candidatos`,
      );
      await Promise.all([fetchPendingDeposits(), fetchPendingCount(), fetchBookings()]);
    } catch (_e) {
      toast.error("No pudimos ejecutar el sweep");
    } finally {
      setAutoRunBusy(false);
    }
  };

  const openDetail = async (booking) => {
    setSelected(booking);
    setDetail(null);
    setDisputeReason("");
    setDeposit(null);
    setDepositAction(null);
    setPartialAmount("");
    setActionNote("");
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${booking.booking_id}`);
      setDetail(data);
    } catch (_e) {
      setDetail(booking);
    } finally {
      setDetailLoading(false);
    }
    // Fetch deposit state in parallel (only meaningful if deposit_amount > 0).
    if ((booking.deposit_amount || 0) > 0 || (booking.deductible || 0) > 0) {
      setDepositLoading(true);
      try {
        const { data } = await api.get(`/admin/bookings/${booking.booking_id}/deposit`);
        setDeposit(data);
      } catch (_e) {
        setDeposit(null);
      } finally {
        setDepositLoading(false);
      }
    }
  };

  const reloadDeposit = async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/admin/bookings/${selected.booking_id}/deposit`);
      setDeposit(data);
    } catch (_e) { /* ignore */ }
  };

  const runDepositAction = async () => {
    if (!selected || !depositAction) return;
    setDepositActionBusy(true);
    try {
      const base = `/admin/bookings/${selected.booking_id}/deposit`;
      if (depositAction === "release") {
        await api.post(`${base}/release`, { note: actionNote });
        toast.success("Depósito liberado");
      } else if (depositAction === "partial") {
        const amt = parseFloat(partialAmount || "0");
        if (!amt || amt <= 0 || amt >= (deposit?.deposit_amount || 0)) {
          toast.error("Monto inválido. Debe ser mayor a 0 y menor al depósito total.");
          setDepositActionBusy(false);
          return;
        }
        await api.post(`${base}/partial-charge`, { charge_amount_mxn: amt, note: actionNote });
        toast.success("Cargo parcial aplicado");
      } else if (depositAction === "charge") {
        await api.post(`${base}/charge`, { note: actionNote });
        toast.success("Depósito cobrado en su totalidad");
      } else if (depositAction === "dispute") {
        if (!actionNote.trim()) {
          toast.error("La nota es requerida para abrir una disputa");
          setDepositActionBusy(false);
          return;
        }
        await api.post(`${base}/dispute`, { note: actionNote });
        toast.success("Disputa registrada");
      } else if (depositAction === "note") {
        if (!actionNote.trim()) {
          toast.error("La nota no puede estar vacía");
          setDepositActionBusy(false);
          return;
        }
        await api.post(`${base}/note`, { note: actionNote });
        toast.success("Nota agregada");
      }
      setActionNote("");
      setPartialAmount("");
      setDepositAction(null);
      await reloadDeposit();
      // Pending-deposit badge can change after any of these actions —
      // refresh in the background so the shortcut count stays honest.
      fetchPendingCount();
      if (pendingDepositsMode) fetchPendingDeposits();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos completar la acción");
    } finally {
      setDepositActionBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (!window.confirm("¿Cancelar esta reservación? El huésped y anfitrión recibirán notificación.")) return;
    setActing(true);
    try {
      await api.post(`/admin/bookings/${selected.booking_id}/cancel`);
      toast.success("Reservación cancelada");
      setSelected(null);
      await fetchBookings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos cancelar la reservación");
    } finally {
      setActing(false);
    }
  };

  const handleMarkDispute = async () => {
    if (!selected) return;
    if (!disputeReason.trim()) {
      toast.error("Por favor describe la disputa");
      return;
    }
    setActing(true);
    try {
      await api.post(`/admin/bookings/${selected.booking_id}/dispute`, { reason: disputeReason.trim() });
      toast.success("Disputa registrada");
      setSelected(null);
      await fetchBookings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos registrar la disputa");
    } finally {
      setActing(false);
    }
  };

  const filtered = bookings.filter((b) => statusFilter === "all" || b.status === statusFilter);

  return (
    <div className="card-base" data-testid="admin-bookings-tab">
      {/* Top filter bar + shortcut toggles */}
      <div className="flex items-center flex-wrap gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          disabled={pendingDepositsMode}
          className="input-base h-9 text-sm w-auto disabled:opacity-50"
          data-testid="bookings-filter-status"
        >
          <option value="all">Todos los estados</option>
          <option value="pending_payment">Pago pendiente</option>
          <option value="requested">Solicitada</option>
          <option value="confirmed">Confirmada</option>
          <option value="active">Activa</option>
          <option value="completed">Completada</option>
          <option value="canceled">Cancelada</option>
        </select>

        {/* "Depósitos por liberar" shortcut — swaps the table view to a
            focused ops queue. Badge shows the overdue count; upcoming is
            visible inside the panel header. */}
        <Button
          size="sm"
          variant={pendingDepositsMode ? "default" : "outline"}
          onClick={() => setPendingDepositsMode((v) => !v)}
          className={pendingDepositsMode ? "btn-primary" : ""}
          data-testid="pending-deposits-toggle"
        >
          <Shield className="w-3.5 h-3.5 mr-1.5" />
          Depósitos por liberar
          {pendingCount.overdue > 0 && (
            <span
              className="ml-2 inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
              data-testid="pending-deposits-badge"
            >
              {pendingCount.overdue}
            </span>
          )}
        </Button>

        {pendingDepositsMode && (
          <Button
            size="sm"
            variant="outline"
            onClick={runAutoReleaseSweep}
            disabled={autoRunBusy}
            className="ml-auto"
            data-testid="run-auto-release-btn"
            title="Ejecutar el sweep automático (también corre cada hora)"
          >
            {autoRunBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Banknote className="w-3.5 h-3.5 mr-1.5" />}
            Ejecutar sweep ahora
          </Button>
        )}
      </div>

      {/* Pending-deposits queue view */}
      {pendingDepositsMode ? (
        <div data-testid="pending-deposits-view">
          <div className="mb-4 p-3 rounded-xl border border-emerald-200 bg-emerald-50/40 text-sm">
            <p className="font-semibold text-emerald-900 mb-1">
              {pendingDepositsData
                ? `${pendingDepositsData.count} depósito(s) elegibles para liberar`
                : "Cargando..."}
              {pendingCount.upcoming > 0 && (
                <span className="font-normal text-emerald-900/70 ml-2">
                  · {pendingCount.upcoming} próximos (aún en periodo de 72h)
                </span>
              )}
            </p>
            <p className="text-xs text-emerald-900/80 leading-relaxed">
              Lista de reservaciones cuyo depósito está retenido, sin disputa abierta,
              y cuya ventana de 72 horas tras la devolución ya venció. El sweep
              automático corre cada hora a los :30; puedes ejecutarlo manualmente
              o liberar cada depósito individualmente.
            </p>
          </div>
          {pendingDepositsData == null ? (
            <div className="py-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : pendingDepositsData.items.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">
              No hay depósitos pendientes de liberar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reservación</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Huésped</TableHead>
                    <TableHead>Devolución</TableHead>
                    <TableHead>Elegible desde</TableHead>
                    <TableHead className="text-right">Depósito</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingDepositsData.items.map((row) => (
                    <TableRow
                      key={row.booking_id}
                      data-testid={`pending-deposit-row-${row.booking_id}`}
                    >
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        <button
                          type="button"
                          className="text-terracotta hover:underline"
                          onClick={() => openDetail({ booking_id: row.booking_id, deposit_amount: row.deposit_amount })}
                          data-testid={`pending-open-${row.booking_id}`}
                        >
                          {row.booking_id}
                        </button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.vehicle ? `${row.vehicle.make} ${row.vehicle.model}` : "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{row.guest?.name || "—"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.return_date)}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.deposit_release_eligible_at)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap font-medium">
                        ${row.deposit_amount.toLocaleString()} MXN
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => quickRelease(row.booking_id)}
                          disabled={quickReleasingId === row.booking_id}
                          className="btn-primary"
                          data-testid={`quick-release-${row.booking_id}`}
                        >
                          {quickReleasingId === row.booking_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            "Liberar"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="py-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-neutral-500">No hay reservaciones.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Vehículo</TableHead>
                <TableHead>Huésped</TableHead>
                <TableHead>Fechas</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((b) => (
                <TableRow
                  key={b.booking_id}
                  className="cursor-pointer hover:bg-neutral-50"
                  onClick={() => openDetail(b)}
                  data-testid={`booking-row-${b.booking_id}`}
                >
                  <TableCell className="font-mono text-xs">{b.booking_id}</TableCell>
                  <TableCell className="whitespace-nowrap">{b.vehicle?.make} {b.vehicle?.model}</TableCell>
                  <TableCell className="whitespace-nowrap">{b.guest?.name || "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {fmtDateRange(b.pickup_date, b.return_date)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">${b.total_price?.toLocaleString()}</TableCell>
                  <TableCell>{statusBadge(b.payment_status || "pending", "pay")}</TableCell>
                  <TableCell>{statusBadge(b.status, "booking")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle>Reservación {selected?.booking_id}</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : detail ? (
            <div className="space-y-6 text-sm">
              {/* Status pills */}
              <div className="flex flex-wrap gap-2">
                {statusBadge(detail.status, "booking")}
                {statusBadge(detail.payment_status || "pending", "pay")}
              </div>

              <Section title="Huésped" icon={UserIcon}>
                <KV k="Nombre" v={detail.guest?.name} />
                <KV k="Email" v={detail.guest?.email} mono />
                <KV k="Teléfono" v={detail.guest?.phone} mono />
                <KV k="Guest ID" v={detail.guest_id} mono />
              </Section>

              <Section title="Anfitrión" icon={UserIcon}>
                <KV k="Nombre" v={detail.host?.name} />
                <KV k="Email" v={detail.host?.email} mono />
                <KV k="Teléfono" v={detail.host?.phone} mono />
                <KV k="Host ID" v={detail.host_id} mono />
              </Section>

              <Section title="Vehículo" icon={FileText}>
                <KV k="Marca / Modelo" v={`${detail.vehicle?.make || "—"} ${detail.vehicle?.model || ""} ${detail.vehicle?.year || ""}`} />
                <KV k="Vehicle ID" v={detail.vehicle_id} mono />
              </Section>

              <Section title="Fechas y entrega" icon={MapPin}>
                <KV k="Recolección" v={fmtDate(detail.pickup_date)} />
                <KV k="Devolución" v={fmtDate(detail.return_date)} />
                <KV k="Tipo" v={detail.pickup_type === "delivery" ? "Entrega a domicilio" : "En ubicación del anfitrión"} />
                <KV k="Dirección de entrega" v={detail.delivery_address} />
              </Section>

              <Section title="Pago y desglose" icon={CreditCard}>
                <KV k="Base" v={`$${(detail.base_price || 0).toLocaleString()} MXN`} />
                <KV k="Cuota de servicio" v={`$${(detail.platform_fee || 0).toLocaleString()} MXN`} />
                <KV k="Plan de protección" v={`${detail.protection_plan || "—"} — $${(detail.protection_fee || 0).toLocaleString()} MXN`} />
                <KV k="Entrega" v={detail.delivery_fee ? `$${detail.delivery_fee.toLocaleString()} MXN` : "—"} />
                <KV k="Impuestos" v={`$${(detail.tax || 0).toLocaleString()} MXN`} />
                <KV k="Total cobrado" v={`$${(detail.total_price || 0).toLocaleString()} MXN`} />
                <KV k="Depósito reembolsable" v={detail.deductible ? `$${detail.deductible.toLocaleString()} MXN (no cobrado)` : "—"} />
                <div className="border-t my-2" />
                <KV k="Comisión RivoNes" v={detail.platform_fee_centavos ? `$${(detail.platform_fee_centavos / 100).toLocaleString()} MXN` : "—"} />
                <KV k="Payout al anfitrión" v={detail.host_payout_centavos ? `$${(detail.host_payout_centavos / 100).toLocaleString()} MXN` : "—"} />
                <KV k="Stripe Session" v={detail.stripe_session_id} mono />
              </Section>

              <Section title="Estado y tiempos" icon={FileText}>
                <KV k="Estado" v={detail.status} />
                <KV k="Estado de pago" v={detail.payment_status} />
                <KV k="Creado" v={fmtDate(detail.created_at)} />
                <KV k="Pagado" v={fmtDate(detail.paid_at)} />
              </Section>

              {/* ----- Deposit Controls ----- */}
              {((detail.deposit_amount || 0) > 0 || (detail.deductible || 0) > 0) && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="admin-deposit-panel">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="label-overline flex items-center gap-2 text-emerald-900">
                      <Shield className="w-3.5 h-3.5" />
                      Depósito reembolsable
                    </p>
                    {deposit && depositStatusBadge(deposit.deposit_status)}
                  </div>
                  {depositLoading ? (
                    <div className="py-4 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
                  ) : deposit ? (
                    <>
                      <div className="space-y-1.5 mb-4">
                        <KV k="Monto" v={`$${(deposit.deposit_amount || 0).toLocaleString()} MXN`} />
                        <KV k="Estado" v={deposit.deposit_status} />
                        <KV k="Retenido desde" v={fmtDate(deposit.deposit_held_at)} />
                        <KV k="Elegible para liberar" v={fmtDate(deposit.deposit_release_eligible_at)} />
                        <KV k="Liberado en" v={fmtDate(deposit.deposit_released_at)} />
                        <KV k="Cargado (centavos)" v={deposit.deposit_charged_centavos ? `$${(deposit.deposit_charged_centavos / 100).toLocaleString()} MXN` : null} />
                        <KV k="Reembolsado (centavos)" v={deposit.deposit_refunded_centavos ? `$${(deposit.deposit_refunded_centavos / 100).toLocaleString()} MXN` : null} />
                        <KV k="Disputa abierta" v={deposit.has_dispute ? "Sí" : null} />
                      </div>

                      {/* Action buttons — only available when deposit_status='held'. */}
                      {deposit.deposit_status === "held" && !depositAction && (
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => setDepositAction("release")} className="btn-primary" data-testid="deposit-release-btn">
                            <Banknote className="w-3.5 h-3.5 mr-1.5" />
                            Liberar completo
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDepositAction("partial")} data-testid="deposit-partial-btn">
                            <Scale className="w-3.5 h-3.5 mr-1.5" />
                            Cargo parcial
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDepositAction("charge")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="deposit-charge-btn">
                            Cargar completo
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDepositAction("dispute")} className="border-amber-400 text-amber-900" data-testid="deposit-dispute-btn">
                            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                            Disputa
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDepositAction("note")} data-testid="deposit-note-btn">
                            <StickyNote className="w-3.5 h-3.5 mr-1.5" />
                            Agregar nota
                          </Button>
                        </div>
                      )}

                      {/* Action form */}
                      {depositAction && (
                        <div className="mt-3 rounded-lg border bg-white p-3 space-y-2" data-testid="deposit-action-form">
                          <p className="text-sm font-semibold">
                            {depositAction === "release" && "Confirmar liberación total del depósito"}
                            {depositAction === "partial" && "Cargo parcial — el resto se reembolsa al huésped"}
                            {depositAction === "charge" && "Cobrar el depósito completo (no reembolso)"}
                            {depositAction === "dispute" && "Abrir disputa (bloquea liberación automática)"}
                            {depositAction === "note" && "Agregar nota interna"}
                          </p>
                          {depositAction === "partial" && (
                            <div>
                              <label className="text-xs text-neutral-500">Monto a cobrar (MXN)</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                max={deposit.deposit_amount || 0}
                                value={partialAmount}
                                onChange={(e) => setPartialAmount(e.target.value)}
                                className="input-base mt-1 h-9 text-sm w-full"
                                placeholder="Ej. 3500"
                                data-testid="deposit-partial-input"
                              />
                              <p className="text-[11px] text-neutral-400 mt-1">
                                Máximo: ${(deposit.deposit_amount || 0).toLocaleString()} MXN. El resto se reembolsa.
                              </p>
                            </div>
                          )}
                          <Textarea
                            placeholder={
                              depositAction === "dispute"
                                ? "Describe la disputa (requerido)"
                                : "Nota interna (opcional)"
                            }
                            value={actionNote}
                            onChange={(e) => setActionNote(e.target.value)}
                            rows={2}
                            className="resize-none text-sm"
                            data-testid="deposit-action-note"
                          />
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              onClick={runDepositAction}
                              disabled={depositActionBusy}
                              className="btn-primary"
                              data-testid="deposit-action-confirm"
                            >
                              {depositActionBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                              Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setDepositAction(null); setActionNote(""); setPartialAmount(""); }}
                              disabled={depositActionBusy}
                              data-testid="deposit-action-cancel"
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Notes timeline */}
                      {(deposit.deposit_notes || []).length > 0 && (
                        <div className="mt-4 border-t pt-3">
                          <p className="text-xs font-semibold text-emerald-900 mb-2">Historial</p>
                          <ul className="space-y-2 max-h-48 overflow-y-auto">
                            {(deposit.deposit_notes || []).slice().reverse().map((n, i) => (
                              <li key={i} className="text-xs text-neutral-700 bg-white rounded-md border p-2">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-semibold text-neutral-600">{depositActionLabel(n.action)}</span>
                                  <span className="text-neutral-400">{fmtDate(n.at)}</span>
                                </div>
                                {typeof n.amount_centavos === "number" && n.amount_centavos > 0 && (
                                  <div className="text-[11px] text-neutral-500">Monto: ${(n.amount_centavos / 100).toLocaleString()} MXN</div>
                                )}
                                {n.note && <div className="mt-1 whitespace-pre-wrap">{n.note}</div>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-neutral-500">
                      Esta reservación no tiene depósito activo todavía
                      (el huésped aún no ha pagado).
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/inbox/${detail.booking_id}`, "_blank")}
                    data-testid="booking-message-host"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Abrir conversación interna
                  </Button>
                  {detail.stripe_session_id && (
                    <a
                      href={`https://dashboard.stripe.com/${process.env.NODE_ENV === "production" ? "" : "test/"}payments`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 hover:bg-neutral-50"
                    >
                      Ver en Stripe
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {!["canceled", "completed"].includes(detail.status) && (
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={acting}
                    className="border-red-300 text-red-600 hover:bg-red-50 w-full sm:w-auto"
                    data-testid="booking-cancel-btn"
                  >
                    {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Cancelar reservación
                  </Button>
                )}
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Marcar disputa / incidencia
                  </p>
                  <Textarea
                    placeholder="Describe la incidencia (visible para el equipo)"
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    rows={2}
                    className="resize-none mb-2 text-sm"
                    data-testid="booking-dispute-reason"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleMarkDispute}
                    disabled={acting || !disputeReason.trim()}
                    className="border-amber-400 text-amber-900"
                    data-testid="booking-mark-dispute"
                  >
                    Registrar disputa
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <p className="label-overline mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v, mono = false }) {
  if (v === null || v === undefined || v === "" || v === "—") return null;
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-neutral-500 w-44 shrink-0">{k}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-neutral-800 break-all`}>{v}</span>
    </div>
  );
}

function depositStatusBadge(status) {
  const map = {
    pending: ["bg-neutral-100 text-neutral-600", "Pendiente"],
    none: ["bg-neutral-100 text-neutral-500", "Sin depósito"],
    held: ["bg-emerald-100 text-emerald-800", "Retenido"],
    released: ["bg-blue-100 text-blue-700", "Liberado"],
    partially_charged: ["bg-amber-100 text-amber-800", "Parcialmente cobrado"],
    charged: ["bg-red-100 text-red-700", "Cobrado"],
    disputed: ["bg-orange-100 text-orange-800", "En disputa"],
  };
  const [cls, label] = map[status] || ["bg-neutral-100 text-neutral-600", status || "—"];
  return <Badge className={cls}>{label}</Badge>;
}

function depositActionLabel(action) {
  return {
    released_full: "Liberación total",
    partial_charge: "Cargo parcial",
    charged_full: "Cobro total",
    marked_disputed: "Disputa abierta",
    note: "Nota",
  }[action] || action;
}

function statusBadge(status, kind) {
  const map = {
    booking: {
      pending_payment: ["bg-yellow-100 text-yellow-800", "Pago pendiente"],
      requested:       ["bg-yellow-100 text-yellow-700", "Solicitada"],
      confirmed:       ["bg-green-100 text-green-700", "Confirmada"],
      active:          ["bg-blue-100 text-blue-700", "Activa"],
      completed:       ["bg-purple-100 text-purple-700", "Completada"],
      canceled:        ["bg-red-100 text-red-700", "Cancelada"],
      cancelled:       ["bg-red-100 text-red-700", "Cancelada"],
      payment_failed:  ["bg-red-100 text-red-700", "Pago fallido"],
    },
    pay: {
      paid:           ["bg-green-100 text-green-700", "Pagado"],
      pending:        ["bg-yellow-100 text-yellow-700", "Pendiente"],
      failed:         ["bg-red-100 text-red-700", "Fallido"],
      refunded:       ["bg-neutral-100 text-neutral-700", "Reembolsado"],
    },
  };
  const [cls, label] = (map[kind] || {})[status] || ["bg-neutral-100 text-neutral-600", status || "—"];
  return <Badge className={cls}>{label}</Badge>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return iso;
  }
}

function fmtDateRange(a, b) {
  try {
    return `${format(new Date(a), "dd MMM", { locale: es })} – ${format(new Date(b), "dd MMM", { locale: es })}`;
  } catch (_e) {
    return "—";
  }
}
